import * as React from 'react';

/**
 * WEB-647 — the app-chrome geometry contract (DPL-01 / DPL-18).
 *
 * Deliberately NOT a `'use client'` module. The RSC `AppShellLayout` imports
 * `STICK_TOP_VAR` from here; behind a `'use client'` boundary that read would
 * resolve to a client-reference proxy instead of the string. As a plain shared
 * module the server gets the real constant and `BottomNav` (already
 * `'use client'`) pulls the hook into the client bundle.
 *
 * Discovery v2 needs to position against the app chrome: the results bar
 * sticks *under* the top bar on desktop, and at ≤720 it leaves scroll flow and
 * sits directly above the nav dock. The handoff assumed Global Nav v2 published
 * `--gn-dock-h` + `.o-gnav-stick-below`; it never did. These are the real names.
 *
 * | Handoff name          | Real name          | Owner                            |
 * | --------------------- | ------------------ | -------------------------------- |
 * | `--gn-dock-h`         | `--nav-dock-h`     | `BottomNav` (measured)           |
 * | (none)                | `--nav-stick-top`  | `AppShellLayout` / globals       |
 * | `.o-gnav-stick-below` | `.nav-stick-below` | `styles/surfaces/app-chrome.css` |
 *
 * The dock height is **measured**, never a hardcoded live value — a frozen
 * pixel height is the failure mode this contract exists to prevent. The one
 * exception is the first-paint *floor* (`DOCK_HEIGHT_PAINT_FLOOR`), used **on
 * the app host only** while the token is still the `:root` `0px` fallback
 * (WEB-768), matching `SHELL_BOTTOM_RESERVATION_CLASS`. Since WEB-1081 retired
 * the ≤720 floor results bar, its CSS consumer is the app-host Locus launcher
 * lift (`styles/overrides/discover-locus-clearance.css`).
 * The measured band is the `<nav>` element, not the inner pill, because the
 * `env(safe-area-inset-bottom)` gutter lives on the `<nav>` padding: a consumer
 * at `bottom: var(--nav-dock-h)` must clear the iOS home indicator too.
 */

/** Live height of the authed bottom-nav band, including its safe-area gutter. */
export const DOCK_HEIGHT_VAR = '--nav-dock-h';

/** Sticky offset for an element sitting directly under the top chrome. */
export const STICK_TOP_VAR = '--nav-stick-top';

/**
 * What `--nav-dock-h` resolves to when no dock renders. Mirrors the `:root`
 * fallback declared in `styles/tokens/color.css`; keep the two in step.
 */
export const DOCK_HEIGHT_ABSENT = '0px';

/**
 * WEB-768 — first-paint floor for ≤720 `bottom: var(--nav-dock-h)` consumers.
 *
 * `:root` declares the token as `0px` until `usePublishedDockHeight` writes
 * the measured band. A bare `bottom: var(--nav-dock-h)` therefore parks a
 * dock-relative element on the viewport bottom for that first frame and
 * overlaps the dock by the full band — 77px at 320×568 dark (WEB-691).
 *
 * `4.8125rem` is 77px at the 16px root, the measured 390×844 band this
 * contract already documents. The live token takes over the moment it
 * exceeds the floor (safe-area, font scale). Keep in step with the
 * `max(4.8125rem, var(--nav-dock-h))` in the **app-host** ≤720 Locus lift
 * (`body:has([data-testid='app-shell-root'])…`). The city host has no dock and
 * must not consume this floor (WEB-796). WEB-1081: the Discover results bar is
 * sticky under the nav at every width and no longer reads the dock at all.
 */
export const DOCK_HEIGHT_PAINT_FLOOR = '4.8125rem';

/**
 * WEB-727 — the bottom band `AppShellLayout` reserves on `<main>` so page
 * content clears the mobile dock **and** the raised Locus FAB.
 *
 * ## Why a `max()` and not a bare `pb-[var(--nav-dock-h)]`
 *
 * `--nav-dock-h` is `0px` until after hydration: `:root` declares the fallback
 * and `BottomNav` publishes the measured value from an effect. A reservation
 * that read the token alone would first-paint every app page with ~zero bottom
 * space and gain ~77–109px when the effect runs. On a long page that shift is
 * below the fold; on a **short** one (empty Saved, a sparse Profile) the footer
 * is on screen, so it is a visible jump and real CLS.
 *
 * The floor keeps first paint exactly where it is today and lets the measured
 * value take over the moment it exceeds the floor — so a dock that grows
 * (larger type scale, a taller `env(safe-area-inset-bottom)`, a future sixth
 * slot) is tracked instead of clipped. The literal survives, but as a **floor**
 * rather than as the truth.
 *
 * ## What the FAB is owed, and why it is a separate term
 *
 * `--nav-dock-h` measures the `<nav>` band only — the inner pill plus its 14px
 * gutter and safe-area inset. The FAB is a 74px disc (`LOCUS_FAB_DIAMETER_PX`,
 * `overlay/locus/LocusLauncher`) pinned 4px above the pill's own baseline inside
 * that band, so it stands 78px from that baseline against a pill measuring
 * ~63px at the default type scale: it **overhangs the band it is measured
 * against by ~15px**. Both quantities carry the safe-area gutter identically, so
 * that overhang is a constant, not a per-device value.
 *
 * `1.25rem` (20px) is that overhang rounded up, which also buys the disc a few
 * pixels of breathing room instead of letting the last row sit flush under it.
 * It is deliberately additive rather than folded into the floor — collapsing
 * the two would make a growing dock silently eat the FAB's clearance.
 *
 * At 390×844 today (`--nav-dock-h: 77px`) the two terms resolve to
 * `max(112px, 97px) = 112px` — pixel-for-pixel the `pb-28` this replaced, before
 * and after hydration alike.
 *
 * Written as one literal string because Tailwind's JIT scans source text: a
 * class assembled from interpolated parts compiles to nothing. `_` is
 * Tailwind's escape for the spaces CSS requires around `calc()`'s `+`.
 */
export const SHELL_BOTTOM_RESERVATION_CLASS = 'pb-[max(7rem,calc(var(--nav-dock-h)_+_1.25rem))]';

/**
 * Publish `--nav-dock-h` on `<html>` from the live size of `ref`.
 *
 * `<html>` rather than the dock's own host element: custom properties inherit
 * downward, and every consumer (the Discover results bar included) lives in a
 * sibling subtree of the dock, so a value scoped to the dock would be
 * unreadable where it is actually needed.
 *
 * A zero-height measurement means "no dock is rendering" — which is precisely
 * what `md:hidden` (`display: none`, WEB-636) produces — so it publishes the
 * documented `0px` fallback rather than a stale height.
 *
 * Client path uses `useLayoutEffect` so ≤720 consumers are not stuck on the
 * `:root` `0px` fallback for a frame. `useLayoutEffect` warns during SSR, so
 * the server path stays `useEffect` (WEB-768).
 */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

export function usePublishedDockHeight(ref: React.RefObject<HTMLElement | null>): void {
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const root = document.documentElement;

    const publish = (): void => {
      const { height } = element.getBoundingClientRect();
      root.style.setProperty(
        DOCK_HEIGHT_VAR,
        height > 0 ? `${Math.round(height)}px` : DOCK_HEIGHT_ABSENT,
      );
    };

    publish();

    // Covers the dock's own size changing. Scroll-retreat is a transform on the
    // inner pill and does not resize the band (deliberately — the offset must
    // not jump mid-scroll), but font scaling and safe-area changes do.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => publish());
    observer?.observe(element);

    // ResizeObserver does not reliably report the transition *into*
    // `display: none`, which is exactly the `md` breakpoint crossing, so the
    // viewport listener is what guarantees the `0px` fallback at `md+`.
    window.addEventListener('resize', publish);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      // Fall back to the `:root` declaration rather than pinning a stale height.
      root.style.removeProperty(DOCK_HEIGHT_VAR);
    };
  }, [ref]);
}
